/**
 * Absences (היעדרויות) — pure helpers shared by the wizard check, the
 * calendar views and the absences panel. No Firebase here.
 *
 * Absence doc (clinics/{clinicId}/absences/{id}):
 *   { therapistId, therapistName, startDate "YYYY-MM-DD", endDate "YYYY-MM-DD",
 *     allDay: bool, startTime "HH:MM"|null, endTime "HH:MM"|null,
 *     type: "vacation"|"sick"|"other", note: string|null,
 *     createdBy, createdByName, createdAt, updatedAt }
 *
 * Visibility is fixed (no setting): peers see only "לא זמין/ה"; type + note
 * render only for admins and the therapist themself (client-side).
 */

export const ABSENCE_TYPES = {
  VACATION: "vacation",
  SICK: "sick",
  OTHER: "other",
};

export const ABSENCE_TYPE_LABELS = {
  vacation: "חופשה",
  sick: "מחלה",
  other: "אחר",
};

export const ABSENCE_PEER_LABEL = "לא זמין/ה";

export const ABSENCE_NOTE_MAX = 120;

const toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// Absence dates/times are Israel wall-clock ("YYYY-MM-DD" / "HH:MM"), so any
// Date → string derivation must go through Asia/Jerusalem — never the
// browser's local zone (a therapist abroad would otherwise match the wrong day).
const ISRAEL_TZ = "Asia/Jerusalem";
const israelParts = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: ISRAEL_TZ,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const p = {};
  for (const part of dtf.formatToParts(date)) p[part.type] = part.value;
  // Some engines render "24" for midnight even with h23; normalise.
  if (p.hour === "24") p.hour = "00";
  return p;
};

/** "YYYY-MM-DD" of the instant in Asia/Jerusalem. */
export const toIsraelDateStr = (d) => {
  const p = israelParts(d);
  if (!p) return "";
  return `${p.year}-${p.month}-${p.day}`;
};

/** "HH:MM" of the instant in Asia/Jerusalem. */
export const toIsraelHHMM = (d) => {
  const p = israelParts(d);
  if (!p) return "";
  return `${p.hour}:${p.minute}`;
};

/**
 * "YYYY-MM-DD" for a Date — Israel calendar day (alias kept for the many
 * existing callers; it was browser-local before).
 */
export const toLocalDateStr = toIsraelDateStr;

/** True when the absence covers the calendar day `dateStr`. */
export function absenceCoversDate(absence, dateStr) {
  if (!absence || !dateStr) return false;
  return absence.startDate <= dateStr && absence.endDate >= dateStr;
}

/**
 * Does an absence block the slot [dateStr startHHMM, +durationMin)?
 * All-day ⇒ any slot on a covered day. Partial ⇒ time windows overlap.
 * Partial absences spanning several days apply the same window every day.
 */
export function absenceBlocksSlot(absence, dateStr, startHHMM, durationMin) {
  if (!absenceCoversDate(absence, dateStr)) return false;
  if (absence.allDay !== false) return true;
  const slotStart = toMinutes(startHHMM);
  const absStart = toMinutes(absence.startTime);
  const absEnd = toMinutes(absence.endTime);
  if (slotStart === null || absStart === null || absEnd === null) return true;
  const slotEnd = slotStart + (parseInt(durationMin, 10) || 0);
  return slotStart < absEnd && absStart < slotEnd;
}

/** First absence (if any) that blocks the slot. */
export function findBlockingAbsence(absences, dateStr, startHHMM, durationMin) {
  if (!Array.isArray(absences) || absences.length === 0) return null;
  return (
    absences.find((a) => absenceBlocksSlot(a, dateStr, startHHMM, durationMin)) ||
    null
  );
}

/** Absences for one therapist on one day (for calendar bands). */
export function absencesForDay(absences, dateStr, therapistId = null) {
  if (!Array.isArray(absences)) return [];
  return absences.filter(
    (a) =>
      absenceCoversDate(a, dateStr) &&
      (!therapistId || a.therapistId === therapistId),
  );
}

/**
 * Label shown on a calendar band / chip. Details (type, note) only when the
 * viewer may see them; peers get the generic label.
 */
export function absenceLabel(absence, { canSeeDetails = false, withName = false } = {}) {
  const base = canSeeDetails
    ? ABSENCE_TYPE_LABELS[absence?.type] || ABSENCE_PEER_LABEL
    : ABSENCE_PEER_LABEL;
  if (withName && absence?.therapistName) return `${absence.therapistName} · ${base}`;
  return base;
}

/** Can this viewer see type + note of the absence? */
export function canSeeAbsenceDetails(absence, { viewerId, isAdmin }) {
  return isAdmin === true || (!!viewerId && absence?.therapistId === viewerId);
}

/** Validate an absence form. Returns { field: hebrewError } (empty ⇒ valid). */
export function validateAbsenceInput(input) {
  const errors = {};
  if (!input?.startDate) errors.startDate = "יש לבחור תאריך התחלה";
  if (!input?.endDate) errors.endDate = "יש לבחור תאריך סיום";
  if (input?.startDate && input?.endDate && input.endDate < input.startDate) {
    errors.endDate = "תאריך הסיום חייב להיות אחרי תאריך ההתחלה";
  }
  if (input?.allDay === false) {
    const s = toMinutes(input.startTime);
    const e = toMinutes(input.endTime);
    if (s === null) errors.startTime = "יש לבחור שעת התחלה";
    if (e === null) errors.endTime = "יש לבחור שעת סיום";
    if (s !== null && e !== null && e <= s) errors.endTime = "שעת הסיום חייבת להיות אחרי שעת ההתחלה";
  }
  if (!Object.values(ABSENCE_TYPES).includes(input?.type)) errors.type = "יש לבחור סוג היעדרות";
  if (input?.note && input.note.length > ABSENCE_NOTE_MAX) {
    errors.note = `הערה עד ${ABSENCE_NOTE_MAX} תווים`;
  }
  return errors;
}

/** "12/03–15/03" or "12/03" for the absence date range. */
export function formatAbsenceRange(absence) {
  const fmt = (s) => {
    if (!s) return "";
    const [, m, d] = s.split("-");
    return `${d}/${m}`;
  };
  if (!absence) return "";
  if (absence.startDate === absence.endDate) return fmt(absence.startDate);
  return `${fmt(absence.startDate)}–${fmt(absence.endDate)}`;
}
